import { Component } from "@angular/core";
import { IonNav, NavParams, PopoverController, LoadingController } from "@ionic/angular";
import { NavigationService, PageName } from "@omni/services/navigation/navigation.service";
import { UIService } from "@omni/services/ui/ui.service";
import { ConsentTerm } from "../../../classes/consent/consent-term.class";
import { ConsentActivity } from "../../../classes/consent/consent-activity.class";
import { Topic } from "../../../classes/consent/topic.class";
import { Product } from "../../../classes/consent/product.class";
import { TranslateService } from "@ngx-translate/core";
import { DatePipe } from "@angular/common";
import { DateTimeFormatsService } from "@omni/services/date-time-formats/date-time-formats.service";
import { IndSectionHeaderViewDataModel } from "@omni/models/indSectionHeaderDataModel";
import { IndTabsDataModel } from "@omni/models/ind-tabs-data-model";
import { DeviceService } from "@omni/services/device/device.service";
import { ConsentService } from "@omni/services/consent/consent.service";
import { ItemDetailCardDataModel } from "../../../models/item-detail-card.model";
import { ContactOfflineService } from "@omni/services/contact/contact.service";
import { AuthenticationService } from "@omni/services/authentication.service";
import { IndAlertMsgComponent } from '@omni/components/shared/ind-alert-msg/ind-alert-msg';
import { IoConfigurationFeatureCodes } from "@omni/classes/authentication/user.class";
import { ChannelActivityType } from "@omni/classes/consent/channel.class";
import { EntityNameForTranslation, LocalizationService } from '@omni/services/localization/localization.service';
import { cloneDeep } from 'lodash-es';
/**
 * Component for displaying consent term details with hierarchical product-channel structure
 */
// OnPush deferred — reads contactService, authenticationService directly in template
@Component({
  selector: "consent-term-details[base-page]",
  templateUrl: "consent-term-details.html",
  styleUrls: ['consent-term-details.scss']
})
export class ConsentTermDetailsComponent {
  trackById(index: number, item: any) { return item?.id || item?.ID || index; }

  private readonly NO_PRODUCTS_TAB_VALUE = 'NO_PRODUCTS';
  
  public consents: ConsentTerm[] = [];
  public isPending: boolean = false;
  public contactId: string = null;
  public selectedTerm: ConsentTerm = null;
  public selectedTermIndex: string = "0";
  public selectedProduct: string = "";
  public selectedProductIndex: string = "0";
  public selectedChannel: string = "";
  public selectedChannelIndex: number = 0;
  public selectedTopics: ItemDetailCardDataModel[] = [];
  public pageTitle: string = "";
  public backButton = [{
    id: "close",
    icon: "chevron-back-outline",
    isDisabled: false,
    align: "left"
  }];
  public termsHeaderModel: IndSectionHeaderViewDataModel;
  public productsHeaderModel: IndSectionHeaderViewDataModel;
  public termTabsData: IndTabsDataModel[] = [];
  public productTabsData: IndTabsDataModel[] = [];

  // Data structures for hierarchical display
  public termProductsMap: Map<string, Set<string>> = new Map(); // termId -> Set of product names
  public termProductChannelsMap: Map<string, Map<string, Set<string>>> = new Map(); // termId -> productName -> Set of channel values (without prefix)
  public termChannelsMap: Map<string, Set<string>> = new Map(); // termId -> Set of channel values (for when no products, without prefix)
  // Store activities mapped by term+product+channel for quick lookup (using raw activity objects)
  public termProductChannelActivitiesMap: Map<string, Map<string, Map<string, any[]>>> = new Map(); // termId -> productName -> channelValue -> activities[]
  public termChannelActivitiesMap: Map<string, Map<string, any[]>> = new Map(); // termId -> channelValue -> activities[] (for when no products)
  // Topic maps
  public termProductChannelTopicsMap: Map<string, Map<string, Map<string, Set<string>>>> = new Map(); // termId -> productName -> channelValue -> Set of topic IDs
  public termChannelTopicsMap: Map<string, Map<string, Set<string>>> = new Map(); // termId -> channelValue -> Set of topic IDs (for when no products)
  // Store topic activities mapped by term+product+channel+topic
  public termProductChannelTopicActivitiesMap: Map<string, Map<string, Map<string, Map<string, any[]>>>> = new Map(); // termId -> productName -> channelValue -> topicId -> activities[]
  public termChannelTopicActivitiesMap: Map<string, Map<string, Map<string, any[]>>> = new Map(); // termId -> channelValue -> topicId -> activities[] (for when no products)
  public fetchRTMEnabled: boolean = false;

  constructor(
    public navCtrl: IonNav,
    public navParams: NavParams,
    private navService: NavigationService,
    private translate: TranslateService,
    private datePipe: DatePipe,
    private dateTimeFormatService: DateTimeFormatsService,
    public device: DeviceService,
    private consentService: ConsentService,
    public contactService: ContactOfflineService,
    public authenticationService: AuthenticationService,
    private popoverCtrl: PopoverController,
    public loadingCtrl: LoadingController,
    private readonly localizationService: LocalizationService,
    private readonly uiService: UIService,
  ) {
    if (this.navParams && this.navParams.data) {
      this.consents = this.navParams.data.consents || [];
      this.isPending = this.navParams.data.isPending || false;
      this.contactId = this.navParams.data.contactId || null;
    }
  }

  private openConsentDetails(term: ConsentTerm) {
    this.uiService.consentFlipButton = false;
    this.consentService.selectedConsentTerm.next(term);
    this.consentService.consentTab = "";
    this.navService.pushWithPageTracking(PageName.ConsentDetailsComponent, PageName.ConsentDetailsComponent, {from: PageName.ConsentTermDetailsComponent});
  }

  ngOnInit() {
    this.fetchRTMEnabled = this.authenticationService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.BU_CONSENT_GA_FETCHRTM) as boolean;
    this.pageTitle = this.translate.instant('ALL_CONSENT_TERMS');
    this.consentService.loadConsentTermsFromDB();
    if (this.consents.length > 0) {
      this.selectedTermIndex = "0";
      this.selectedTerm = this.consents[0];
      if (this.isPending) {
        this.processPendingSyncConsentActivities();
      } else {
        this.processConsentActivities();
      }
      this.initTermTabs();
      this.initProductTabs();
      this.initSelectedChannel();
    }

    this.initSectionHeaders();
  }

  private initSectionHeaders() {
    this.termsHeaderModel = {
      id: 'terms-header',
      title: this.translate.instant('CONTACT_CONSENT_TERMS'),
      controls: [],
    };

    this.productsHeaderModel = {
      id: 'products-header',
      title: this.translate.instant('PRODUCTS_CAP'),
      controls: [],
    };
  }

  private initTermTabs() {
    this.termTabsData = this.consents.map((term, index) => ({
      displayText: term.indskr_consentTermName || '',
      value: index.toString(),
      disable: false
    }));
  }

  private initProductTabs() {
    // Only initialize product tabs if isProductConsent is enabled
    if (!this.authenticationService.user.isProductConsent) {
      this.productTabsData = [];
      this.selectedProductIndex = "";
      this.selectedProduct = "";
      this.initSelectedChannel();
      return;
    }
    
    const products = this.getSelectedTermProducts();
    const channelsWithoutProducts = this.getSelectedTermChannels();
    const hasBothProductsAndChannels = products.length > 0 && channelsWithoutProducts.length > 0;
    
    this.productTabsData = products.map((product, index) => ({
      displayText: product,
      value: index.toString(),
      disable: false
    }));
    
    // Add NO_PRODUCTS tab if both products and channels without products exist
    if (hasBothProductsAndChannels) {
      this.productTabsData.push({
        displayText: this.translate.instant('NO_PRODUCTS'),
        value: this.NO_PRODUCTS_TAB_VALUE,
        disable: false
      });
    }
    
    if (products.length > 0) {
      this.selectedProductIndex = "0";
      this.selectedProduct = products[0];
      this.initSelectedChannel();
    } else {
      this.selectedProductIndex = "";
      this.selectedProduct = "";
      this.initSelectedChannel(); // Still initialize channel if there are channels without products
    }
  }

  private initSelectedChannel(preserveChannel?: string) {
    let channels: string[] = [];
    if (this.selectedProduct) {
      channels = this.getSelectedProductChannelValues();
    } else {
      channels = this.getSelectedTermChannels();
    }
    
    if (channels.length > 0) {
      if (preserveChannel && channels.includes(preserveChannel)) {
        const index = channels.indexOf(preserveChannel);
        this.selectedChannelIndex = index;
        this.selectedChannel = preserveChannel;
      } else {
        this.selectedChannelIndex = 0;
        this.selectedChannel = channels[0];
      }
    } else {
      this.selectedChannelIndex = 0;
      this.selectedChannel = "";
    }
    
    // Compute and cache topics for the selected channel
    this.updateSelectedTopics();
  }

  private processConsentActivities() {
    this.termProductsMap.clear();
    this.termProductChannelsMap.clear();
    this.termChannelsMap.clear();
    this.termProductChannelActivitiesMap.clear();
    this.termChannelActivitiesMap.clear();
    this.termProductChannelTopicsMap.clear();
    this.termChannelTopicsMap.clear();
    this.termProductChannelTopicActivitiesMap.clear();
    this.termChannelTopicActivitiesMap.clear();

    // Get all consent activities from the service
    const allActivities = this.consentService.allConsentActivitiesWithProductsAndEmails;
    if (!allActivities || allActivities.length === 0) {
      return;
    }

    // Get contactId from navParams (passed from contact-consent component)
    if (!this.contactId) {
      return;
    }

    // Filter activities by contactId first
    const contactActivities = allActivities.filter(activity => 
      activity.contactId && activity.contactId.toString() === this.contactId.toString()
    );

    this.consents.forEach(consent => {
      const termId = consent.indskr_consenttermsid;
      const productSet = new Set<string>();
      const productChannelsMap = new Map<string, Set<string>>();
      const channelSet = new Set<string>(); // For channels without products
      const productChannelActivitiesMap = new Map<string, Map<string, any[]>>();
      const channelActivitiesMap = new Map<string, any[]>();
      // Topic maps
      const productChannelTopicsMap = new Map<string, Map<string, Set<string>>>(); // productName -> channelValue -> Set of topic IDs
      const channelTopicsMap = new Map<string, Set<string>>(); // channelValue -> Set of topic IDs (for when no products)
      const productChannelTopicActivitiesMap = new Map<string, Map<string, Map<string, any[]>>>(); // productName -> channelValue -> topicId -> activities[]
      const channelTopicActivitiesMap = new Map<string, Map<string, any[]>>(); // channelValue -> topicId -> activities[] (for when no products)

      // Filter activities for this specific term
      const termActivities = contactActivities.filter(activity => {
        return activity.consentTermId && activity.consentTermId.toString() === termId.toString();
      });

      termActivities.forEach(rawActivity => {
        // Filter by opt-in (100000000) or opt-out (100000001) activity types
        const activityType = rawActivity.indskr_type;
        if (activityType !== 100000000 && activityType !== 100000001) {
          return;
        }
          // Use raw activity directly - no conversion needed
        const channelValue = rawActivity.emailAddress || rawActivity.indskr_phone || 
                           rawActivity.indskr_faxnumber || rawActivity.indskr_addressname;
        
        // Get channel type from formatted value
        const channelType = rawActivity['consentTypeId@OData.Community.Display.V1.FormattedValue'] || 
                           rawActivity['indskr_consentTypeName'] || '';
        
        // Create composite key: channelType|channelValue to handle same value with different channel types
          // If channelType is empty, still create key with empty type part to maintain structure
        const channelKey = channelValue ? (channelType ? `${channelType}|${channelValue}` : `|${channelValue}`) : null;
        
        if (!channelKey) {
          return; // Early continue if no channel key
        }
        // Add product if exists and isProductConsent is enabled
        const hasProduct = rawActivity.indskr_productname && this.authenticationService.user.isProductConsent;
        if (hasProduct) {
          this.processProductChannelActivity(
            rawActivity,
            channelKey,
            productSet,
            productChannelsMap,
            productChannelActivitiesMap,
            productChannelTopicsMap,
            productChannelTopicActivitiesMap
          );
        }
        
        // Add channel without product (when product consent is disabled or no product in activity)
        const shouldProcessChannelWithoutProduct = !rawActivity.indskr_productname || !this.authenticationService.user.isProductConsent;
        if (shouldProcessChannelWithoutProduct) {
          this.processChannelWithoutProductActivity(
            rawActivity,
            channelKey,
            channelSet,
            channelActivitiesMap,
            channelTopicsMap,
            channelTopicActivitiesMap
          );
        }
      });

      // Only store products if isProductConsent is enabled
      if (productSet.size > 0 && this.authenticationService.user.isProductConsent) {
        this.termProductsMap.set(termId.toString(), productSet);
        this.termProductChannelsMap.set(termId.toString(), productChannelsMap);
        this.termProductChannelActivitiesMap.set(termId.toString(), productChannelActivitiesMap);
        this.termProductChannelTopicsMap.set(termId.toString(), productChannelTopicsMap);
        this.termProductChannelTopicActivitiesMap.set(termId.toString(), productChannelTopicActivitiesMap);
      }
      
      // Store channels if they exist (even when products exist, to support NO_PRODUCTS tab)
      if (channelSet.size > 0) {
        this.termChannelsMap.set(termId.toString(), channelSet);
        this.termChannelActivitiesMap.set(termId.toString(), channelActivitiesMap);
        this.termChannelTopicsMap.set(termId.toString(), channelTopicsMap);
        this.termChannelTopicActivitiesMap.set(termId.toString(), channelTopicActivitiesMap);
      }
    });
  }

  private processProductChannelActivity(
    rawActivity: any,
    channelKey: string,
    productSet: Set<string>,
    productChannelsMap: Map<string, Set<string>>,
    productChannelActivitiesMap: Map<string, Map<string, any[]>>,
    productChannelTopicsMap: Map<string, Map<string, Set<string>>>,
    productChannelTopicActivitiesMap: Map<string, Map<string, Map<string, any[]>>>
  ): void {
    const productName = rawActivity.indskr_productname;
    productSet.add(productName);
    
    // Initialize product channels map if needed
    if (!productChannelsMap.has(productName)) {
      productChannelsMap.set(productName, new Set<string>());
    }
    if (!productChannelActivitiesMap.has(productName)) {
      productChannelActivitiesMap.set(productName, new Map<string, any[]>());
    }
  
    // Add channel key under product (composite key: channelType|channelValue)
    productChannelsMap.get(productName).add(channelKey);
    
    // Store activity for this channel
    if (!productChannelActivitiesMap.get(productName).has(channelKey)) {
      productChannelActivitiesMap.get(productName).set(channelKey, []);
    }
    productChannelActivitiesMap.get(productName).get(channelKey).push(rawActivity);
  
    // Process topics for product-channel combo
    const topicId = rawActivity['_indskr_topic_value'] || rawActivity.indskr_topic || rawActivity.topic;
    const rawTopicName = rawActivity['_indskr_topic_value@OData.Community.Display.V1.FormattedValue'] || 
                          rawActivity.topicName || 
                        rawActivity.indskr_topic_Formatted;
    const topicName = rawTopicName ? this.localizationService.getTranslatedString(
      rawTopicName, 
      EntityNameForTranslation.Topic
    ) : '';
    
    if (!topicId || !topicName) {
      return; // Early return if no topic
    }
  
    // Initialize topic maps if needed
    if (!productChannelTopicsMap.has(productName)) {
      productChannelTopicsMap.set(productName, new Map<string, Set<string>>());
    }
    if (!productChannelTopicsMap.get(productName).has(channelKey)) {
      productChannelTopicsMap.get(productName).set(channelKey, new Set<string>());
    }
    productChannelTopicsMap.get(productName).get(channelKey).add(topicId);
  
    // Store topic activities
    if (!productChannelTopicActivitiesMap.has(productName)) {
      productChannelTopicActivitiesMap.set(productName, new Map<string, Map<string, any[]>>());
    }
    if (!productChannelTopicActivitiesMap.get(productName).has(channelKey)) {
      productChannelTopicActivitiesMap.get(productName).set(channelKey, new Map<string, any[]>());
    }
    if (!productChannelTopicActivitiesMap.get(productName).get(channelKey).has(topicId)) {
      productChannelTopicActivitiesMap.get(productName).get(channelKey).set(topicId, []);
    }
    productChannelTopicActivitiesMap.get(productName).get(channelKey).get(topicId).push(rawActivity);
  }

  private processChannelWithoutProductActivity(
    rawActivity: any,
    channelKey: string,
    channelSet: Set<string>,
    channelActivitiesMap: Map<string, any[]>,
    channelTopicsMap: Map<string, Set<string>>,
    channelTopicActivitiesMap: Map<string, Map<string, any[]>>
  ): void {
    // Add channel without product (when product consent is disabled)
    channelSet.add(channelKey);
    
    // Store activity for this channel
    if (!channelActivitiesMap.has(channelKey)) {
      channelActivitiesMap.set(channelKey, []);
    }
    channelActivitiesMap.get(channelKey).push(rawActivity);
  
    // Process topics for channel (no product)
    const topicId = rawActivity['_indskr_topic_value'] || rawActivity.indskr_topic || rawActivity.topic;
    const rawTopicName = rawActivity['_indskr_topic_value@OData.Community.Display.V1.FormattedValue'] || 
                          rawActivity.topicName || 
                          rawActivity.indskr_topic_Formatted;
    const topicName = rawTopicName ? this.localizationService.getTranslatedString(
      rawTopicName, 
      EntityNameForTranslation.Topic
    ) : '';
    if (!topicId || !topicName) {
      return; // Early return if no topic
    }
  
    // Initialize topic maps if needed
    if (!channelTopicsMap.has(channelKey)) {
      channelTopicsMap.set(channelKey, new Set<string>());
    }
    channelTopicsMap.get(channelKey).add(topicId);
  
    // Store topic activities
    if (!channelTopicActivitiesMap.has(channelKey)) {
      channelTopicActivitiesMap.set(channelKey, new Map<string, any[]>());
    }
    if (!channelTopicActivitiesMap.get(channelKey).has(topicId)) {
      channelTopicActivitiesMap.get(channelKey).set(topicId, []);
    }
    channelTopicActivitiesMap.get(channelKey).get(topicId).push(rawActivity);
  }

  private processPendingSyncConsentActivities() {
    this.termProductsMap.clear();
    this.termProductChannelsMap.clear();
    this.termChannelsMap.clear();
    this.termProductChannelActivitiesMap.clear();
    this.termChannelActivitiesMap.clear();
    this.termProductChannelTopicsMap.clear();
    this.termChannelTopicsMap.clear();
    this.termProductChannelTopicActivitiesMap.clear();
    this.termChannelTopicActivitiesMap.clear();

    this.consents.forEach(consent => {
      const termId = consent.indskr_consenttermsid;
      if (!termId) {
        return;
      }

      const productSet = new Set<string>();
      const productChannelsMap = new Map<string, Set<string>>();
      const channelSet = new Set<string>(); // For channels without products
      const productChannelActivitiesMap = new Map<string, Map<string, any[]>>();
      const channelActivitiesMap = new Map<string, any[]>();
      // Topic maps
      const productChannelTopicsMap = new Map<string, Map<string, Set<string>>>(); // productName -> channelValue -> Set of topic IDs
      const channelTopicsMap = new Map<string, Set<string>>(); // channelValue -> Set of topic IDs (for when no products)
      const productChannelTopicActivitiesMap = new Map<string, Map<string, Map<string, any[]>>>(); // productName -> channelValue -> topicId -> activities[]
      const channelTopicActivitiesMap = new Map<string, Map<string, any[]>>(); // channelValue -> topicId -> activities[] (for when no products)

      // Get topics that match each channel from mappings
      const channelTopicsMap_temp = new Map<string, any[]>(); // channelId -> topics[]
      if (consent.topics && consent.topics.length > 0) {
        // If no mappings, apply all topics to all channels
        const allTopics = consent.topics.map(topic => ({
          indskr_topic: topic.indskr_topic,
          indskr_topicname: topic.indskr_topicname
        }));
        if (consent.channels && consent.channels.length > 0) {
          consent.channels.forEach(channel => {
            if (channel.activityType === ChannelActivityType.EMAIL || 
              channel.activityType === ChannelActivityType.SMS) {
              channelTopicsMap_temp.set(channel.indskr_consenttypeid.toString(), allTopics);
            }
          });
        }
      }

      // Process channels
      if (consent.channels && consent.channels.length > 0) {
        consent.channels.forEach(channel => {
          if (!channel.values || channel.values.length === 0) {
            return;
          }

          const channelType = channel.indskr_consentType || '';
          const channelTypeId = (channel.indskr_consenttypeid || '').toString();

          // Get topics for this channel
          const channelTopics = channelTopicsMap_temp.get(channelTypeId) || [];

          // Process each checked channel value
          channel.values.forEach(channelValue => {
            if (!channelValue.isChecked || !channelValue.value) {
              return;
            }

            // Map channel value to appropriate field based on channel type
            let emailAddress: string = '';
            let indskr_phone: string = '';
            let indskr_faxnumber: string = '';
            let indskr_addressname: string = '';

            const channelTypeLower = channelType.toLowerCase();
            if (channelTypeLower.includes('email')) {
              emailAddress = channelValue.value;
            } else if (channelTypeLower.includes('phone')) {
              indskr_phone = channelValue.value;
            } else if (channelTypeLower.includes('fax')) {
              indskr_faxnumber = channelValue.value;
            } else if (channelTypeLower.includes('address') || channelTypeLower.includes('visit')) {
              indskr_addressname = channelValue.value;
            }

            // Get channel value for composite key
            const channelValueStr = emailAddress || indskr_phone || indskr_faxnumber || indskr_addressname;
            const channelKey = channelValueStr ? (channelType ? `${channelType}|${channelValueStr}` : `|${channelValueStr}`) : null;

            if (!channelKey) {
              return;
            }

            // Get products for this consent
            const checkedProducts = (consent.products && consent.products.length > 0 && this.authenticationService.user.isProductConsent)
              ? consent.products.filter(p => p.isChecked)
              : [];

            // Create activities for each product (if product consent enabled) or without product
            if (checkedProducts.length > 0) {
              // Process with products
              checkedProducts.forEach(product => {
                const productName = product.indskr_productname;
                if (!productName) {
                  return;
                }

                productSet.add(productName);

                // Initialize product channels map if needed
                if (!productChannelsMap.has(productName)) {
                  productChannelsMap.set(productName, new Set<string>());
                }
                if (!productChannelActivitiesMap.has(productName)) {
                  productChannelActivitiesMap.set(productName, new Map<string, any[]>());
                }

                productChannelsMap.get(productName).add(channelKey);

                // Create activity for product-channel combo
                const baseActivity: any = {
                  indskr_type: 100000000, // opt-in
                  consentTermId: termId.toString(),
                  contactId: consent.indskr_contactid || this.contactId,
                  createdon: consent.createdon,
                  indskr_activitydate: consent.createdon,
                  emailAddress: emailAddress,
                  indskr_phone: indskr_phone,
                  indskr_faxnumber: indskr_faxnumber,
                  indskr_addressname: indskr_addressname,
                  'consentTypeId@OData.Community.Display.V1.FormattedValue': channelType,
                  indskr_consentTypeName: channelType,
                  indskr_productname: productName
                };

                // Store activity for this channel
                if (!productChannelActivitiesMap.get(productName).has(channelKey)) {
                  productChannelActivitiesMap.get(productName).set(channelKey, []);
                }
                productChannelActivitiesMap.get(productName).get(channelKey).push(baseActivity);

                // Process topics for product-channel combo
                if (channelTopics.length > 0) {
                  // Initialize topic maps if needed
                  if (!productChannelTopicsMap.has(productName)) {
                    productChannelTopicsMap.set(productName, new Map<string, Set<string>>());
                  }
                  if (!productChannelTopicsMap.get(productName).has(channelKey)) {
                    productChannelTopicsMap.get(productName).set(channelKey, new Set<string>());
                  }

                  if (!productChannelTopicActivitiesMap.has(productName)) {
                    productChannelTopicActivitiesMap.set(productName, new Map<string, Map<string, any[]>>());
                  }
                  if (!productChannelTopicActivitiesMap.get(productName).has(channelKey)) {
                    productChannelTopicActivitiesMap.get(productName).set(channelKey, new Map<string, any[]>());
                  }

                  channelTopics.forEach(topic => {
                    const topicId = topic.indskr_topic;
                    const rawTopicName = topic.indskr_topicname;
                    if (topicId && rawTopicName) {
                      const topicName = this.localizationService.getTranslatedString(
                        rawTopicName, 
                        EntityNameForTranslation.Topic
                      );
                      productChannelTopicsMap.get(productName).get(channelKey).add(topicId);

                      // Create activity for product-channel-topic combo
                      const topicActivity = {
                        ...baseActivity,
                        _indskr_topic_value: topicId,
                        indskr_topic: topicId,
                        topic: topicId,
                        '_indskr_topic_value@OData.Community.Display.V1.FormattedValue': topicName,
                        topicName: topicName,
                        indskr_topic_Formatted: topicName
                      };

                      if (!productChannelTopicActivitiesMap.get(productName).get(channelKey).has(topicId)) {
                        productChannelTopicActivitiesMap.get(productName).get(channelKey).set(topicId, []);
                      }
                      productChannelTopicActivitiesMap.get(productName).get(channelKey).get(topicId).push(topicActivity);
                    }
                  });
                }
              });
            } else {
              // Process without products (product consent disabled or no products)
              channelSet.add(channelKey);

              // Create base activity for channel (no product)
              const baseActivity: any = {
                indskr_type: 100000000, // opt-in
                consentTermId: termId.toString(),
                contactId: consent.indskr_contactid || this.contactId,
                createdon: consent.createdon,
                indskr_activitydate: consent.createdon,
                emailAddress: emailAddress,
                indskr_phone: indskr_phone,
                indskr_faxnumber: indskr_faxnumber,
                indskr_addressname: indskr_addressname,
                'consentTypeId@OData.Community.Display.V1.FormattedValue': channelType,
                indskr_consentTypeName: channelType
              };

              // Store activity for this channel
              if (!channelActivitiesMap.has(channelKey)) {
                channelActivitiesMap.set(channelKey, []);
              }
              channelActivitiesMap.get(channelKey).push(baseActivity);

              // Process topics for channel (no product)
              if (channelTopics.length > 0) {
                // Initialize topic maps if needed
                if (!channelTopicsMap.has(channelKey)) {
                  channelTopicsMap.set(channelKey, new Set<string>());
                }

                if (!channelTopicActivitiesMap.has(channelKey)) {
                  channelTopicActivitiesMap.set(channelKey, new Map<string, any[]>());
                }

                channelTopics.forEach(topic => {
                  const topicId = topic.indskr_topic;
                  const rawTopicName = topic.indskr_topicname;
                  if (topicId && rawTopicName) {
                    const topicName = this.localizationService.getTranslatedString(
                      rawTopicName, 
                      EntityNameForTranslation.Topic
                    );
                    channelTopicsMap.get(channelKey).add(topicId);

                    // Create activity for channel-topic combo
                    const topicActivity = {
                      ...baseActivity,
                      _indskr_topic_value: topicId,
                      indskr_topic: topicId,
                      topic: topicId,
                      '_indskr_topic_value@OData.Community.Display.V1.FormattedValue': topicName,
                      topicName: topicName,
                      indskr_topic_Formatted: topicName
                    };

                    if (!channelTopicActivitiesMap.get(channelKey).has(topicId)) {
                      channelTopicActivitiesMap.get(channelKey).set(topicId, []);
                    }
                    channelTopicActivitiesMap.get(channelKey).get(topicId).push(topicActivity);
                  }
                });
              }
            }
          });
        });
      }

      // Only store products if isProductConsent is enabled
      if (productSet.size > 0 && this.authenticationService.user.isProductConsent) {
        this.termProductsMap.set(termId.toString(), productSet);
        this.termProductChannelsMap.set(termId.toString(), productChannelsMap);
        this.termProductChannelActivitiesMap.set(termId.toString(), productChannelActivitiesMap);
        this.termProductChannelTopicsMap.set(termId.toString(), productChannelTopicsMap);
        this.termProductChannelTopicActivitiesMap.set(termId.toString(), productChannelTopicActivitiesMap);
      }

      // Store channels if they exist (even when products exist, to support NO_PRODUCTS tab)
      if (channelSet.size > 0) {
        this.termChannelsMap.set(termId.toString(), channelSet);
        this.termChannelActivitiesMap.set(termId.toString(), channelActivitiesMap);
        this.termChannelTopicsMap.set(termId.toString(), channelTopicsMap);
        this.termChannelTopicActivitiesMap.set(termId.toString(), channelTopicActivitiesMap);
      }
    });
  }

  public selectTerm(value: string) {
    const index = parseInt(value, 10);
    if (index >= 0 && index < this.consents.length) {
      this.selectedTermIndex = value;
      this.selectedTerm = this.consents[index];
      this.initProductTabs();
    }
  }

  public selectProduct(value: string) {
    // Check if NO_PRODUCTS tab was selected
    if (value === this.NO_PRODUCTS_TAB_VALUE) {
      this.selectedProductIndex = value;
      this.selectedProduct = this.NO_PRODUCTS_TAB_VALUE;
      this.initSelectedChannel();
      return;
    }
    
    const products = this.getSelectedTermProducts();
    const index = parseInt(value, 10);
    if (index >= 0 && index < products.length) {
      this.selectedProductIndex = value;
      this.selectedProduct = products[index];
      this.initSelectedChannel();
    }
  }

  public selectChannel(channelValue: string, index: number) {
    this.selectedChannel = channelValue;
    this.selectedChannelIndex = index;
    // Compute and cache topics when channel is selected
    this.updateSelectedTopics();
  }

  private updateSelectedTopics() {
    if (this.selectedChannel) {
      this.selectedTopics = this.getTopicsForSelectedCombo();
    } else {
      this.selectedTopics = [];
    }
  }

  public getSelectedTermProducts(): string[] {
    if (!this.selectedTerm || !this.authenticationService.user.isProductConsent) return [];
    const termId = this.selectedTerm.indskr_consenttermsid;
    const products = this.termProductsMap.get(termId.toString());
    return products ? Array.from(products).sort() : [];
  }

  public getProductChannels(productName: string): string[] {
    if (!this.selectedTerm || !this.authenticationService.user.isProductConsent) return [];
    const termId = this.selectedTerm.indskr_consenttermsid;
    const productChannelsMap = this.termProductChannelsMap.get(termId.toString());
    if (productChannelsMap && productChannelsMap.has(productName)) {
      return Array.from(productChannelsMap.get(productName)).sort();
    }
    return [];
  }

  public getSelectedProductChannels(): string[] {
    if (!this.selectedProduct || !this.authenticationService.user.isProductConsent) {
      return this.getSelectedTermChannels();
    }
    
    // If NO_PRODUCTS is selected, return channels without products
    if (this.selectedProduct === this.NO_PRODUCTS_TAB_VALUE) {
      return this.getSelectedTermChannels();
    }
    
    return this.getProductChannels(this.selectedProduct);
  }

  public getSelectedProductChannelValues(): string[] {
    if (!this.selectedProduct || !this.authenticationService.user.isProductConsent) {
      return this.getSelectedTermChannels();
    }
    
    // If NO_PRODUCTS is selected, return channels without products
    if (this.selectedProduct === this.NO_PRODUCTS_TAB_VALUE) {
      return this.getSelectedTermChannels();
    }
    
    return this.getProductChannels(this.selectedProduct);
  }

  public getSelectedTermChannels(): string[] {
    if (!this.selectedTerm) return [];
    const termId = this.selectedTerm.indskr_consenttermsid;
    const channels = this.termChannelsMap.get(termId.toString());
    return channels ? Array.from(channels).sort() : [];
  }

  /**
   * Extract channel type from composite key (channelType|channelValue)
   */
  private getChannelTypeFromKey(channelKey: string): string {
    if (!channelKey || !channelKey.includes('|')) return '';
    const parts = channelKey.split('|');
    return parts[0] || '';
  }

  /**
   * Extract channel value from composite key (channelType|channelValue)
   */
  private getChannelValueFromKey(channelKey: string): string {
    if (!channelKey || !channelKey.includes('|')) return channelKey;
    const parts = channelKey.split('|');
    return parts.slice(1).join('|') || ''; // Join in case value contains |
  }

  public getChannelTypeLabel(channelKey: string): string {
    const channelType = this.getChannelTypeFromKey(channelKey);
    if (channelType) {
      return this.consentService.displayChannelLabels(channelType);
    }
    return '';
  }

  public getChannelValue(channelKey: string): string {
    return this.getChannelValueFromKey(channelKey);
  }

  /**
   * Check if channel is in active consent records (selectedTerm.channels)
   */
  private isChannelInActiveConsentRecords(channelValue: string): boolean {
    if (!this.selectedTerm || !this.selectedTerm.channels || !channelValue) {
      return false;
    }

    const channelValueToMatch = this.getChannelValueFromKey(channelValue);
    const channelTypeToMatch = this.getChannelTypeFromKey(channelValue);

    // Loop through channels in selectedTerm
    for (const channel of this.selectedTerm.channels) {
      // Match by channel type if available
      if (channelTypeToMatch) {
        const channelType = channel.indskr_consentType || '';
        if (channelType && !channelType.toLowerCase().includes(channelTypeToMatch.toLowerCase()) && 
            !channelTypeToMatch.toLowerCase().includes(channelType.toLowerCase())) {
          continue; // Channel type doesn't match
        }
      }

      // Check if channel value exists in channel.values[].value
      if (channel.values && channel.values.length > 0) {
        for (const channelVal of channel.values) {
          if (channelVal.value && channelVal.value.toString() === channelValueToMatch) {
            return true; // Channel found in active consent records
          }
        }
      }
    }

    return false;
  }

  /**
   * Check if all topics are opted out (no topic is opted in)
   */
  private areAllTopicsOptedOut(channelValue: string): boolean {
    if (!this.selectedTopics || this.selectedTopics.length === 0) {
      return false;
    }
    for (const topic of this.selectedTopics) {
      const topicStatus = this.getTopicStatus(topic.itemId);
      if (topicStatus === this.translate.instant('OPTED_IN')) {
        return false; // At least one topic is opted in
      }
    }

    return true; // All topics are opted out
  }

  /**
   * Get last opt-out activity with no subsequent opt-in activities
   */
  private getLastOptOutWithNoSubsequentOptIn(channelValue: string): any {
    const activities = this.getActivitiesForChannel(channelValue);
    if (activities.length === 0) {
      return null;
    }

    // Sort activities by date (most recent first)
    const sortedActivities = activities.sort((a, b) => {
      const dateA = this.getActivityDate(a);
      const dateB = this.getActivityDate(b);
      return dateB - dateA;
    });

    // Find the most recent opt-out activity
    const optOutActivities = sortedActivities.filter(a => a.indskr_type === 100000001);
    if (optOutActivities.length === 0) {
      return null;
    }

    // Get the most recent opt-out activity
    const mostRecentOptOut = optOutActivities[0];
    const optOutDate = this.getActivityDate(mostRecentOptOut);

    // Check if there are any opt-in activities after this opt-out date
    const optInActivities = sortedActivities.filter(a => a.indskr_type === 100000000);
    for (const optInActivity of optInActivities) {
      const optInDate = this.getActivityDate(optInActivity);
      if (optInDate > optOutDate) {
        // There's an opt-in after the opt-out, so this opt-out is not valid
        return null;
      }
    }

    // No opt-in activities exist after the opt-out, return the opt-out activity
    return mostRecentOptOut;
  }

  public getExpiresOnDate(): string {
    if (!this.selectedTerm || !this.selectedTerm.indskr_untildate) return '';
    return this.datePipe.transform(
      this.selectedTerm.indskr_untildate, 
      this.dateTimeFormatService.date, 
      undefined, 
      this.translate.currentLang
    );
  }

  public getSignatureValue(): string {
    if (!this.selectedTerm) return this.translate.instant('NO');
    return this.selectedTerm.indskr_signature 
      ? this.translate.instant('YES') 
      : this.translate.instant('NO');
  }

  public getActivitiesForChannel(channelValue: string): any[] {
    if (!this.selectedTerm || !channelValue) return [];
    const termId = this.selectedTerm.indskr_consenttermsid;
    
    // If isProductConsent is false, NO_PRODUCTS is selected, or no product selected, get activities from term-channel map
    if (!this.authenticationService.user.isProductConsent || !this.selectedProduct || this.selectedProduct === this.NO_PRODUCTS_TAB_VALUE) {
      const channelActivitiesMap = this.termChannelActivitiesMap.get(termId.toString());
      if (channelActivitiesMap && channelActivitiesMap.has(channelValue)) {
        return channelActivitiesMap.get(channelValue) || [];
      }
    } else {
      // Product is selected and isProductConsent is enabled, get activities from product-channel map
      const productChannelActivitiesMap = this.termProductChannelActivitiesMap.get(termId.toString());
      if (productChannelActivitiesMap && productChannelActivitiesMap.has(this.selectedProduct)) {
        const channelActivitiesMap = productChannelActivitiesMap.get(this.selectedProduct);
        if (channelActivitiesMap && channelActivitiesMap.has(channelValue)) {
          return channelActivitiesMap.get(channelValue) || [];
        }
      }
    }
    
    return [];
  }

  public getChannelStatus(channelValue: string): string {
    const activities = this.getActivitiesForChannel(channelValue);
    if (activities.length === 0) return '';
    
    // Sort by date (most recent first)
    const sortedActivities = activities.sort((a, b) => {
      const dateA = this.getActivityDate(a);
      const dateB = this.getActivityDate(b);
      return dateB - dateA;
    });
    
    const latestActivity = sortedActivities[0];
    if (latestActivity.indskr_type === 100000000) {
      return this.translate.instant('OPTED_IN');
    } else if (latestActivity.indskr_type === 100000001) {
      return this.translate.instant('OPTED_OUT');
    }
    return '';
  }

  public getOptedInDate(channelValue: string): string {
    const activities = this.getActivitiesForChannel(channelValue);
    const optInActivities = activities.filter(a => a.indskr_type === 100000000);
    if (optInActivities.length === 0) return '--';
    
    // Get latest opt-in date
    const sorted = optInActivities.sort((a, b) => {
      const dateA = this.getActivityDate(a);
      const dateB = this.getActivityDate(b);
      return dateB - dateA;
    });
    
    const date = this.getActivityDate(sorted[0]);
    if (!date) return '--';
    
    return this.datePipe.transform(
      new Date(date),
      this.dateTimeFormatService.date,
      undefined,
      this.translate.currentLang
    ) || '--';
  }

  public getOptedInSource(channelValue: string): string {
    const activities = this.getActivitiesForChannel(channelValue);
    const optInActivities = activities.filter(a => a.indskr_type === 100000000);
    if (optInActivities.length === 0) return '--';
    
    // Get latest opt-in source
    const sorted = optInActivities.sort((a, b) => {
      const dateA = this.getActivityDate(a);
      const dateB = this.getActivityDate(b);
      return dateB - dateA;
    });
    
    const latest = sorted[0];
    let source = '';
    
    if (latest.indskr_optinsource_Formatted) {
      source = latest.indskr_optinsource_Formatted;
    } else if (latest.indskr_consentactivitysourcetype) {
      source = latest.indskr_consentactivitysourcetype;
    } else if (latest.consentSourceName) {
      source = latest.consentSourceName;
    }
    
    return source || '--';
  }

  public getOptedOutDate(channelValue: string): string {
    // First check: If channel is in active consent records, don't show opt-out info
    if (this.isChannelInActiveConsentRecords(channelValue) && 
      !(this.authenticationService.user.isProductConsent && this.selectedProduct)) {
      return '--';
    }

    // Channel is NOT in active consent records
    // Check if channel has topics
    if (!this.selectedTopics || this.selectedTopics.length === 0) {
      // No topics: Check if latest channel activity is opt-out
      const activities = this.getActivitiesForChannel(channelValue);
      if (activities.length === 0) return '--';
      
      // Sort by date (most recent first)
      const sortedActivities = activities.sort((a, b) => {
        const dateA = this.getActivityDate(a);
        const dateB = this.getActivityDate(b);
        return dateB - dateA;
      });
      
      // Check if latest activity is opt-out
      const latestActivity = sortedActivities[0];
      if (latestActivity.indskr_type !== 100000001) {
        return '--'; // Latest activity is not opt-out
      }
      
      // Latest activity is opt-out, return the date
      const date = this.getActivityDate(latestActivity);
      if (!date) return '--';
      
      return this.datePipe.transform(
        new Date(date),
        this.dateTimeFormatService.date,
        undefined,
        this.translate.currentLang
      ) || '--';
    } else {
      // Topics exist: Check if all topics are opted out
      if (!this.areAllTopicsOptedOut(channelValue)) {
        return '--'; // Not all topics are opted out
      }
      
      // All topics are opted out, get the last opt-out activity with no subsequent opt-ins
      const lastOptOutActivity = this.getLastOptOutWithNoSubsequentOptIn(channelValue);
      if (!lastOptOutActivity) {
        return '--';
      }
      
      const date = this.getActivityDate(lastOptOutActivity);
      if (!date) return '--';
      
      return this.datePipe.transform(
        new Date(date),
        this.dateTimeFormatService.date,
        undefined,
        this.translate.currentLang
      ) || '--';
    }
  }

  public getOptedOutSource(channelValue: string): string {
    // First check: If channel is in active consent records, don't show opt-out info
    if (this.isChannelInActiveConsentRecords(channelValue) && 
      !(this.authenticationService.user.isProductConsent && this.selectedProduct)) {
      return '--';
    }

    // Channel is NOT in active consent records
    // Check if channel has topics
    if (!this.selectedTopics || this.selectedTopics.length === 0) {
      // No topics: Check if latest channel activity is opt-out
      const activities = this.getActivitiesForChannel(channelValue);
      if (activities.length === 0) return '--';
      
      // Sort by date (most recent first)
      const sortedActivities = activities.sort((a, b) => {
        const dateA = this.getActivityDate(a);
        const dateB = this.getActivityDate(b);
        return dateB - dateA;
      });
      
      // Check if latest activity is opt-out
      const latestActivity = sortedActivities[0];
      if (latestActivity.indskr_type !== 100000001) {
        return '--'; // Latest activity is not opt-out
      }
      
      // Latest activity is opt-out, return the source
      let source = '';
      if (latestActivity.indskr_optoutsource_Formatted) {
        source = latestActivity.indskr_optoutsource_Formatted;
      } else if (latestActivity.indskr_consentactivitysourcetype) {
        source = latestActivity.indskr_consentactivitysourcetype;
      } else if (latestActivity.consentSourceName) {
        source = latestActivity.consentSourceName;
      }
      
      return source || '--';
    } else {
      // Topics exist: Check if all topics are opted out
      if (!this.areAllTopicsOptedOut(channelValue)) {
        return '--'; // Not all topics are opted out
      }
      
      // All topics are opted out, get the last opt-out activity with no subsequent opt-ins
      const lastOptOutActivity = this.getLastOptOutWithNoSubsequentOptIn(channelValue);
      if (!lastOptOutActivity) {
        return '--';
      }
      
      // Return source from the last opt-out activity
      let source = '';
      if (lastOptOutActivity.indskr_optoutsource_Formatted) {
        source = lastOptOutActivity.indskr_optoutsource_Formatted;
      } else if (lastOptOutActivity.indskr_consentactivitysourcetype) {
        source = lastOptOutActivity.indskr_consentactivitysourcetype;
      } else if (lastOptOutActivity.consentSourceName) {
        source = lastOptOutActivity.consentSourceName;
      }
      
      return source || '--';
    }
  }

  public isChannelOptedIn(channelValue: string): boolean {
    const status = this.getChannelStatus(channelValue);
    return status === this.translate.instant('OPTED_IN');
  }

  public async handleChannelStatusToggleClick(event: { isOptedIn: boolean; childRef?: any }) {
    // Check if channel is opted-out - reset toggle state immediately
    if (!this.selectedChannel || !this.isChannelOptedIn(this.selectedChannel)) {
      return;
    }

    // Create selectedFilters array from selectedProduct and selectedChannel
    const selectedFilters: Array<{label: string, value: string}> = [];
    
    // Add product filter if selected
    if (this.selectedProduct && this.selectedProduct !== this.NO_PRODUCTS_TAB_VALUE) {
      selectedFilters.push({
        label: this.translate.instant('PRODUCTS'),
        value: this.selectedProduct
      });
    }
    
    // Add channel filter if selected
    if (this.selectedChannel) {
      const channelValue = this.getChannelValueFromKey(this.selectedChannel);
      const channelLabel = this.getChannelTypeLabel(this.selectedChannel);
      if (channelLabel && channelValue) {
        selectedFilters.push({
          label: channelLabel,
          value: channelValue
        });
      }
    }
    
    // Create channelText similar to contact-consent.ts
    const channelText = Array.prototype.map.call(selectedFilters, a => `${a.label} <b>"${a.value}"</b>`).join(", ");
    
    // Show confirmation dialog
    const message = this.translate.instant('OPT_OUT_EACH_TERM', { 
      channel: channelText, 
      termName: this.selectedTerm ? this.selectedTerm.indskr_consentTermName : ''
    });
    
    const popoverOptions = {
      header: this.translate.instant('OPT_OUT'),
      message: message,
      confirmText: this.translate.instant('CONFIRM'),
    };

    const popover = await this.popoverCtrl.create({
      component: IndAlertMsgComponent,
      componentProps: popoverOptions,
      cssClass: this.device.isNativeApp ? 'native-ind-alert-msg' : 'ind-alert-msg',
      backdropDismiss: true,
    });
    
    popover.present();
    const res = await popover.onDidDismiss();
      if (res.data?.role == 'ok') {
        const loader = await this.loadingCtrl.create();
        await loader.present();
         // Prepare opt-out payload for channel level
        const optOutPayload = this._prepareChannelOptOutPayload();
        this.consentService.createOptOutConsentActivity([optOutPayload]).then(async () => {
          await loader.dismiss();
          const preserveChannel = this.selectedChannel;
          this.refreshConsentsFromService();
          // Refresh the view by reprocessing activities
          this.processConsentActivities();
          this.initTermTabs();
          this.initProductTabs();
          this.initSelectedChannel(preserveChannel);
        }, async error => {
          await loader.dismiss();
        });
      } else {
        // User cancelled the flow, restore toggle state
        if (event.childRef) {
          event.childRef.revertTo(true);
        }
      }
  }

  public onChannelSignatureClick() {
    if (!this.selectedTerm || !this.selectedChannel) {
      return;
    }

    // Find the consent term
    const term = this.consents.find(c => {
      const termId = c.indskr_consenttermsid;
      const selectedTermId = this.selectedTerm.indskr_consenttermsid;
      return termId.toString() === selectedTermId.toString();
    });

    if (term) {
      // Create a deep clone to avoid modifying the original
      const filteredTerm = cloneDeep(term);
      
      // Populate indskr_body from allConsentTermsSubject
      const allConsentTerms: ConsentTerm[] = this.consentService.allConsentTermsSubject.getValue();
      if (allConsentTerms && allConsentTerms.length > 0) {
        const termId = filteredTerm.indskr_consenttermsid || '';
        const idx = allConsentTerms.findIndex(t => {
          const tId = t.indskr_consenttermsid || '';
          return tId.toString() === termId.toString();
        });
        if (idx > -1) {
          filteredTerm.indskr_body = allConsentTerms[idx].indskr_body ? allConsentTerms[idx].indskr_body : "";
        }
      }

      // Filter products to show only ONE product if productId is specified
      if (this.selectedProduct && this.selectedProduct !== this.NO_PRODUCTS_TAB_VALUE && this.authenticationService.user.isProductConsent) {
        const matchingProduct = filteredTerm.products?.find(
          p => p.indskr_productname === this.selectedProduct
        );
        // If product is not found in filteredTerm.products, construct it from activities
        if (!matchingProduct) {
          const activities = this.getActivitiesForChannel(this.selectedChannel);
          if (activities && activities.length > 0) {
            const productActivity = activities.find(activity => 
              activity.indskr_productname === this.selectedProduct
            );
            if (productActivity && productActivity.indskr_productname) {
              const constructedProduct = new Product(
                productActivity.indskr_product || productActivity['_indskr_product_value'] || '',
                productActivity.indskr_productname,
                true,
                undefined
              );
              filteredTerm.products = [constructedProduct];
            } else {
              filteredTerm.products = [];
            }
          } else {
            filteredTerm.products = [];
          }
        } else {
          filteredTerm.products = [matchingProduct];
        }
      } else {
        // If no product specified or isProductConsent is disabled, clear products
        filteredTerm.products = [];
      }
      
      // Filter channels to show only ONE channel with ONE value
      if (this.selectedChannel) {
        // Extract the channel value from the composite key (channelType|channelValue)
        const expectedChannelValue = this.getChannelValueFromKey(this.selectedChannel);
        const expectedChannelType = this.getChannelTypeFromKey(this.selectedChannel);
        
        // Find the channel that contains the selected channel value
        let matchingChannel = null;
        const isOptedIn = this.isChannelOptedIn(this.selectedChannel);
        if (isOptedIn && filteredTerm.channels) {
          for (const channel of filteredTerm.channels) {
            // Match by channel type first (if available), then by value
            const channelTypeMatches = !expectedChannelType || 
              channel.indskr_consentType === expectedChannelType;
            
            if (channelTypeMatches) {
              const matchingValue = channel.values?.find(v => v.value === expectedChannelValue);
              if (matchingValue) {
                matchingChannel = cloneDeep(channel);
                // Set values to only include the matching one
                matchingChannel.values = [matchingValue];
                break;
              }
            }
          }
        }
        // If channel is opted out, construct channel from activities
        if (!matchingChannel && !isOptedIn) {
          const activities = this.getActivitiesForChannel(this.selectedChannel);
          if (activities && activities.length > 0) {
            const firstActivity = activities[0];
            const consentTypeId = firstActivity['consentTypeId'] || firstActivity['indskr_ConsentType'] || '';
            if (consentTypeId && expectedChannelValue) {
              matchingChannel = {
                indskr_consenttypeid: consentTypeId.toString(),
                indskr_consentType: expectedChannelType || '',
                values: [{
                  value: expectedChannelValue,
                  isChecked: true,
                  indskr_consentId: undefined
                }]
              };
            }
          }
        }
        filteredTerm.channels = matchingChannel ? [matchingChannel] : [];
      }
      
      // Filter consentActivity to only include activities for this product/channel combination (no topic)
      if (filteredTerm.consentActivity) {
        filteredTerm.consentActivity = filteredTerm.consentActivity.filter(activity => {
          // If isProductConsent is disabled, filter out activities with products
          if (!this.authenticationService.user.isProductConsent && activity.indskr_productname) {
            return false;
          }
          // Match product if specified
          if (this.selectedProduct && this.selectedProduct !== this.NO_PRODUCTS_TAB_VALUE && activity.indskr_productname !== this.selectedProduct) {
            return false;
          }
          // Match channel value if specified (channelId is now composite key: channelType|channelValue)
          if (this.selectedChannel) {
            const channelValue = activity.indskr_emailaddress || activity.indskr_phone || 
                                activity.indskr_faxnumber || activity.indskr_addressname;
            const expectedChannelValue = this.getChannelValueFromKey(this.selectedChannel);
            if (channelValue !== expectedChannelValue) {
              return false;
            }
          }
          // Exclude activities with topics (we only want channel-level activities)
          if (activity.indskr_topic || activity["topic"]) {
            return false;
          }
          return true;
        });
      }
      
      // Clear topics array (no topics for channel-level view)
      filteredTerm.topics = [];
      
      // Set the filtered consent term in the service
      this.openConsentDetails(filteredTerm);
    }
  }

  private _buildBaseOptOutPayload(activities: any[]): any {
    const payload: any = {};

    payload['indskr_Contact@odata.bind'] = this.contactService.contactInformation.ID;
    payload['indskr_type'] = 100000001;
    payload['indskr_SalesRep@odata.bind'] = this.authenticationService.user.xSystemUserID;
    payload['indskr_Position@odata.bind'] = this.authenticationService.user.xPositionID;

    if (this.selectedTerm) {
      const termId = this.selectedTerm.indskr_consenttermsid;
      if (termId) {
        payload['indskr_ConsentTerms@odata.bind'] = termId.toString();
      }
      payload['indskr_consentTermName'] = this.selectedTerm.indskr_consentTermName || '';
    }

    const channelValue = this.getChannelValueFromKey(this.selectedChannel);

    let consentTypeId: string = null;
    let consentTypeName: string = '';
    let emailAddress: string = '';
    let addressName: string = '';
    let phone: string = '';

    if (this.selectedTerm && this.selectedTerm.channels && channelValue) {
      for (const channel of this.selectedTerm.channels) {
        const matchingValue = channel.values?.find(v => v.value === channelValue);
        if (matchingValue) {
          consentTypeId = channel.indskr_consenttypeid ? channel.indskr_consenttypeid.toString() : null;
          consentTypeName = this.getChannelTypeLabel(this.selectedChannel);
          if (activities && activities.length > 0) {
            const activity = activities.find(a => {
              const activityChannelValue = a.emailAddress || a.indskr_phone ||
                                           a.indskr_faxnumber || a.indskr_addressname;
              return activityChannelValue === channelValue;
            });
            if (activity) {
              if (activity.emailAddress) emailAddress = activity.emailAddress;
              if (activity.indskr_addressname) addressName = activity.indskr_addressname;
              if (activity.indskr_phone) phone = activity.indskr_phone;
            }
          }
          break;
        }
      }
    }

    if (consentTypeId) {
      payload['indskr_ConsentType@odata.bind'] = consentTypeId;
    }
    payload['indskr_consentTypeName'] = consentTypeName;
    if (this.authenticationService.user.isProductConsent && this.selectedProduct && this.selectedProduct !== this.NO_PRODUCTS_TAB_VALUE && this.selectedTerm) {
      if (this.selectedTerm.products) {
        const product = this.selectedTerm.products.find(p => p.indskr_productname === this.selectedProduct);
        if (product && product.indskr_productid) {
          payload['indskr_Product@odata.bind'] = product.indskr_productid;
        }
      }
      payload['indskr_productname'] = this.selectedProduct;
    } else {
      payload['indskr_productname'] = '';
    }
    if (emailAddress) {
      payload['indskr_EmailAddress@odata.bind'] = emailAddress;
    }
    payload['indskr_addressname'] = addressName;
    payload['indskr_phone'] = phone;
    payload['createdon'] = new Date().getTime();
    return payload;
  }

  private _prepareChannelOptOutPayload(): any {
    return this._buildBaseOptOutPayload(this.getActivitiesForChannel(this.selectedChannel));
  }

  private getActivityDate(activity: any): number {
    if (activity.indskr_activitydate) {
      return new Date(activity.indskr_activitydate).getTime();
    } else if (activity.createdon) {
      return new Date(activity.createdon).getTime();
    }
    return 0;
  }

  /**
   * Gets sorted activities for a topic (most recent first).
   * Returns null if no activities exist, otherwise returns sorted array.
   */
  private getSortedActivitiesForTopic(topicId: string): any[] | null {
    const activities = this.getActivitiesForTopic(topicId);
    if (activities.length === 0) return null;
    
    // Sort by date (most recent first)
    const sortedActivities = activities.sort((a, b) => {
      const dateA = this.getActivityDate(a);
      const dateB = this.getActivityDate(b);
      return dateB - dateA;
    });
    
    return sortedActivities;
  }

  // Topic methods
  public getTopicsForSelectedCombo(): ItemDetailCardDataModel[] {
    if (!this.selectedTerm || !this.selectedChannel) return [];
    
    const termId = this.selectedTerm.indskr_consenttermsid;
    const topicActivitiesMap: Map<string, any[]> = new Map();
    const topicNameMap: Map<string, string> = new Map();

    // Get activities for the selected combo
    let activities: any[] = [];
    // If isProductConsent is false, NO_PRODUCTS is selected, or no product selected, get from channel map (no product)
    if (!this.authenticationService.user.isProductConsent || !this.selectedProduct || this.selectedProduct === this.NO_PRODUCTS_TAB_VALUE) {
      const channelTopicActivitiesMap = this.termChannelTopicActivitiesMap.get(termId.toString());
      if (channelTopicActivitiesMap && channelTopicActivitiesMap.has(this.selectedChannel)) {
        const topicActivities = channelTopicActivitiesMap.get(this.selectedChannel);
        topicActivities.forEach((activitiesList, topicId) => {
          topicActivitiesMap.set(topicId, activitiesList);
          // Get topic name from first activity
          if (activitiesList.length > 0) {
            const rawTopicName  = activitiesList[0]['_indskr_topic_value@OData.Community.Display.V1.FormattedValue'] || 
                             activitiesList[0].topicName || 
                             activitiesList[0].indskr_topic_Formatted;
            if (rawTopicName ) {
              const topicName = this.localizationService.getTranslatedString(
                rawTopicName, 
                EntityNameForTranslation.Topic
              );
              topicNameMap.set(topicId, topicName);
            }
          }
        });
      }
    } else {
      // Get from product-channel map (only when isProductConsent is enabled)
      const productChannelTopicActivitiesMap = this.termProductChannelTopicActivitiesMap.get(termId.toString());
      if (productChannelTopicActivitiesMap && productChannelTopicActivitiesMap.has(this.selectedProduct)) {
        const channelTopicActivitiesMap = productChannelTopicActivitiesMap.get(this.selectedProduct);
        if (channelTopicActivitiesMap && channelTopicActivitiesMap.has(this.selectedChannel)) {
          const topicActivities = channelTopicActivitiesMap.get(this.selectedChannel);
          topicActivities.forEach((activitiesList, topicId) => {
            topicActivitiesMap.set(topicId, activitiesList);
            // Get topic name from first activity
            if (activitiesList.length > 0) {
              const rawTopicName = activitiesList[0]['_indskr_topic_value@OData.Community.Display.V1.FormattedValue'] || 
                               activitiesList[0].topicName || 
                               activitiesList[0].indskr_topic_Formatted;
              if (rawTopicName) {
                const topicName = this.localizationService.getTranslatedString(
                  rawTopicName, 
                  EntityNameForTranslation.Topic
                );
                topicNameMap.set(topicId, topicName);
              }
            }
          });
        }
      }
    }

    // Convert to ItemDetailCardDataModel array
    const topics: ItemDetailCardDataModel[] = [];
    topicActivitiesMap.forEach((activities, topicId) => {
      const topicName = topicNameMap.get(topicId) || '';
      if (topicName) {
        const sortedActivities = this.getSortedActivitiesForTopic(topicId);
        const isOptedIn = this.getTopicStatus(topicId) === this.translate.instant('OPTED_IN');
        const optedInDate = this.getTopicOptedInDate(sortedActivities);
        const optedOutDate = this.getTopicOptedOutDate(sortedActivities);
        const optedInSource = this.getTopicOptedInSource(sortedActivities);
        const optedOutSource = this.getTopicOptedOutSource(sortedActivities);
        const hasSignature = true;

        topics.push({
          itemId: topicId,
          itemName: topicName,
          isOptedIn: isOptedIn,
          optedInDate: optedInDate,
          optedOutDate: optedOutDate,
          optedInSource: optedInSource,
          optedOutSource: optedOutSource,
          hasSignature: hasSignature,
          activities: activities,
          termId: termId.toString(),
          productId: (this.authenticationService.user.isProductConsent && this.selectedProduct && this.selectedProduct !== this.NO_PRODUCTS_TAB_VALUE) ? this.selectedProduct : undefined,
          channelId: this.selectedChannel || undefined,
          topicId: topicId,
          contactId: this.contactId || undefined
        });
      }
    });

    return topics.sort((a, b) => a.itemName.localeCompare(b.itemName));
  }

  public getTopicStatus(topicId: string): string {
    const sortedActivities = this.getSortedActivitiesForTopic(topicId);
    if (!sortedActivities) return '';
    
    const latestActivity = sortedActivities[0];
    if (latestActivity.indskr_type === 100000000) {
      return this.translate.instant('OPTED_IN');
    } else if (latestActivity.indskr_type === 100000001) {
      return this.translate.instant('OPTED_OUT');
    }
    return '';
  }

  public getTopicOptedInDate(sortedActivities: any[] | null): string {
    if (!sortedActivities) return '';
    
    const optInActivities = sortedActivities.filter(a => a.indskr_type === 100000000);
    if (optInActivities.length === 0) return '';
    
    // Get latest opt-in date (already sorted, so first one is latest)
    const date = this.getActivityDate(optInActivities[0]);
    if (!date) return '';
    
    return this.datePipe.transform(
      new Date(date),
      this.dateTimeFormatService.date,
      undefined,
      this.translate.currentLang
    ) || '';
  }

  public getTopicOptedOutDate(sortedActivities: any[] | null): string {
    if (!sortedActivities) return '';
    
    // Check if latest activity is opt-out
    const latestActivity = sortedActivities[0];
    if (latestActivity.indskr_type !== 100000001) {
      return ''; // Latest activity is not opt-out
    }
    
    // Latest activity is opt-out, return the date
    const date = this.getActivityDate(latestActivity);
    if (!date) return '';
    
    return this.datePipe.transform(
      new Date(date),
      this.dateTimeFormatService.date,
      undefined,
      this.translate.currentLang
    ) || '';
  }

  public getTopicOptedInSource(sortedActivities: any[] | null): string {
    if (!sortedActivities) return '--';
    
    const optInActivities = sortedActivities.filter(a => a.indskr_type === 100000000);
    if (optInActivities.length === 0) return '--';
    
    // Get latest opt-in source (already sorted, so first one is latest)
    const latest = optInActivities[0];
    let source = '';
    
    if (latest.indskr_optinsource_Formatted) {
      source = latest.indskr_optinsource_Formatted;
    } else if (latest.indskr_consentactivitysourcetype) {
      source = latest.indskr_consentactivitysourcetype;
    } else if (latest.consentSourceName) {
      source = latest.consentSourceName;
    }
    
    return source || '--';
  }

  public getTopicOptedOutSource(sortedActivities: any[] | null): string {
    if (!sortedActivities) return '--';
    
    // Check if latest activity is opt-out
    const latestActivity = sortedActivities[0];
    if (latestActivity.indskr_type !== 100000001) {
      return '--'; // Latest activity is not opt-out
    }
    
    // Latest activity is opt-out, return the source
    let source = '';
    if (latestActivity.indskr_optoutsource_Formatted) {
      source = latestActivity.indskr_optoutsource_Formatted;
    } else if (latestActivity.indskr_consentactivitysourcetype) {
      source = latestActivity.indskr_consentactivitysourcetype;
    } else if (latestActivity.consentSourceName) {
      source = latestActivity.consentSourceName;
    }
    
    return source || '--';
  }

  private getActivitiesForTopic(topicId: string): any[] {
    if (!this.selectedTerm || !this.selectedChannel || !topicId) return [];
    
    const termId = this.selectedTerm.indskr_consenttermsid;
    
    // If isProductConsent is false, NO_PRODUCTS is selected, or no product selected, get from channel-topic map (no product)
    if (!this.authenticationService.user.isProductConsent || !this.selectedProduct || this.selectedProduct === this.NO_PRODUCTS_TAB_VALUE) {
      const channelTopicActivitiesMap = this.termChannelTopicActivitiesMap.get(termId.toString());
      if (channelTopicActivitiesMap && channelTopicActivitiesMap.has(this.selectedChannel)) {
        const topicActivities = channelTopicActivitiesMap.get(this.selectedChannel);
        if (topicActivities && topicActivities.has(topicId)) {
          return topicActivities.get(topicId) || [];
        }
      }
    } else {
      // Get from product-channel-topic map (only when isProductConsent is enabled)
      const productChannelTopicActivitiesMap = this.termProductChannelTopicActivitiesMap.get(termId.toString());
      if (productChannelTopicActivitiesMap && productChannelTopicActivitiesMap.has(this.selectedProduct)) {
        const channelTopicActivitiesMap = productChannelTopicActivitiesMap.get(this.selectedProduct);
        if (channelTopicActivitiesMap && channelTopicActivitiesMap.has(this.selectedChannel)) {
          const topicActivities = channelTopicActivitiesMap.get(this.selectedChannel);
          if (topicActivities && topicActivities.has(topicId)) {
            return topicActivities.get(topicId) || [];
          }
        }
      }
    }
    
    return [];
  }

  public onSignatureClick(topicData: ItemDetailCardDataModel) {
    // Find the consent term for this topic
  const term = this.consents.find(c => {
    const termId = c.indskr_consenttermsid;
    return termId.toString() === topicData.termId;
  });

  if (term) {
    // Create a deep clone to avoid modifying the original
    // This preserves all properties including indskr_body, indskr_signaturerequired, 
    // indskr_agreementmessage, indskr_allowpaperconsentcapture, etc. needed for terms and signature display
    const filteredTerm = cloneDeep(term);
     // Populate indskr_body from allConsentTermsSubject (similar to openConsentDetails in contact-consent.ts)
     const allConsentTerms: ConsentTerm[] = this.consentService.allConsentTermsSubject.getValue();
     if (allConsentTerms && allConsentTerms.length > 0) {
       const termId = filteredTerm.indskr_consenttermsid || '';
       const idx = allConsentTerms.findIndex(t => {
         const tId = t.indskr_consenttermsid || '';
         return tId.toString() === termId.toString();
       });
       if (idx > -1) {
         filteredTerm.indskr_body = allConsentTerms[idx].indskr_body ? allConsentTerms[idx].indskr_body : "";
       }
     }

    // Filter products to show only ONE product if productId is specified
    if (topicData.productId) {
      const matchingProduct = filteredTerm.products?.find(
        p => p.indskr_productname === topicData.productId
      );
      // If product is not found in filteredTerm.products, construct it from activities
      if (!matchingProduct) {
        let productActivity = null;
        // Try to get from topicData.activities first
        if (topicData.activities && topicData.activities.length > 0) {
          productActivity = topicData.activities.find(activity => 
            activity.indskr_productname === topicData.productId
          );
        }
        // If not found, try getting from activities for the topic
        if (!productActivity && topicData.topicId) {
          const activities = this.getActivitiesForTopic(topicData.topicId);
          if (activities && activities.length > 0) {
            productActivity = activities.find(activity => 
              activity.indskr_productname === topicData.productId
            );
          }
        }
        if (productActivity && productActivity.indskr_productname) {
          const constructedProduct = new Product(
            productActivity.indskr_product || productActivity['_indskr_product_value'] || '',
            productActivity.indskr_productname,
            true,
            undefined
          );
          filteredTerm.products = [constructedProduct];
        } else {
          filteredTerm.products = [];
        }
      } else {
        filteredTerm.products = [matchingProduct];
      }
    } else {
      // If no product specified or isProductConsent is disabled, clear products
      filteredTerm.products = [];
    }
    
    // Filter channels to show only ONE channel with ONE value
    if (topicData.channelId) {
      // Extract the channel value from the composite key (channelType|channelValue)
      const expectedChannelValue = this.getChannelValueFromKey(topicData.channelId);
      const expectedChannelType = this.getChannelTypeFromKey(topicData.channelId);
      
      // Find the channel that contains the selected channel value
      let matchingChannel = null;
      if (topicData.isOptedIn && filteredTerm.channels) {
        for (const channel of filteredTerm.channels) {
          // Match by channel type first (if available), then by value
          const channelTypeMatches = !expectedChannelType || 
            channel.indskr_consentType === expectedChannelType;
          
          if (channelTypeMatches) {
            const matchingValue = channel.values?.find(v => v.value === expectedChannelValue);
            if (matchingValue) {
              matchingChannel = cloneDeep(channel);
              // Set values to only include the matching one
              matchingChannel.values = [matchingValue];
              break;
            }
          }
        }
      }
       // If topic is opted out, construct channel from activities
      if (!matchingChannel && !topicData.isOptedIn && topicData.activities && topicData.activities.length > 0) {
        const firstActivity = topicData.activities[0];
        const consentTypeId = firstActivity['consentTypeId'] || '';
        if (consentTypeId && expectedChannelValue) {
          matchingChannel = {
            indskr_consenttypeid: consentTypeId.toString(),
            indskr_consentType: expectedChannelType || '',
            values: [{
              value: expectedChannelValue,
              isChecked: true,
              indskr_consentId: undefined
            }]
          };
        }
      }
      filteredTerm.channels = matchingChannel ? [matchingChannel] : [];
    }

    // Filter consentActivity to only include activities for this product/channel/topic combination
    if (filteredTerm.consentActivity) {
      filteredTerm.consentActivity = filteredTerm.consentActivity.filter(activity => {
        // If isProductConsent is disabled, filter out activities with products
        if (!this.authenticationService.user.isProductConsent && activity.indskr_productname) {
          return false;
        }
        // Match product if specified
        if (topicData.productId && activity.indskr_productname !== topicData.productId) {
          return false;
        }
        // Match channel value if specified (channelId is now composite key: channelType|channelValue)
        if (topicData.channelId) {
          const channelValue = activity.indskr_emailaddress || activity.indskr_phone || 
                              activity.indskr_faxnumber || activity.indskr_addressname;
          const expectedChannelValue = this.getChannelValueFromKey(topicData.channelId);
          if (channelValue !== expectedChannelValue) {
            return false;
          }
        }
        // Match topic if specified
        if (topicData.topicId) {
          const activityTopicId = activity.indskr_topic || activity["topic"];
          if (activityTopicId !== topicData.topicId) {
            return false;
          }
        }
        return true;
      });
    }
    
    // Create topics array with the specific topic (consent-details expects this property)
    if (topicData.topicId && topicData.itemName) {
      const topic = new Topic(
        topicData.topicId,           // indskr_topic (topic ID)
        topicData.itemName,          // indskr_topicname (topic name)
        true,                        // isChecked = true
        undefined                    // indskr_consentId (optional)
      );
      filteredTerm.topics = [topic];
    }
    
    // Set the filtered consent term in the service
    this.openConsentDetails(filteredTerm);
    }
  }

  public closepage() {
    this.navService.popChildNavPageWithPageTracking();
  }

  public async handleStatusToggleClick(event: Event) {
    const topicData = (event as any).itemData;
    const childRef = (event as any).childRef;
    
    // Only allow opt-out when toggle is checked (opted in)
    if (!topicData.isOptedIn) {
      return;
    }

    // Create selectedFilters array from selectedProduct, selectedChannel, and topicData
    const selectedFilters: Array<{label: string, value: string}> = [];
    
    // Add product filter if selected
    if (this.selectedProduct && this.selectedProduct !== this.NO_PRODUCTS_TAB_VALUE) {
      selectedFilters.push({
        label: this.translate.instant('PRODUCTS'),
        value: this.selectedProduct
      });
    }
    
    // Add channel filter if selected
    if (this.selectedChannel) {
      const channelValue = this.getChannelValueFromKey(this.selectedChannel);
      const channelLabel = this.getChannelTypeLabel(this.selectedChannel);
      if (channelLabel && channelValue) {
        selectedFilters.push({
          label: channelLabel,
          value: channelValue
        });
      }
    }
    
    // Add topic filter if exists
    if (topicData.itemName) {
      selectedFilters.push({
        label: this.translate.instant('TOPICS'),
        value: topicData.itemName
      });
    }
    
    // Create channelText similar to contact-consent.ts
    const channelText = Array.prototype.map.call(selectedFilters, a => `${a.label} <b>"${a.value}"</b>`).join(", ");
    
    // Show confirmation dialog
    const message = this.translate.instant('OPT_OUT_EACH_TERM', { 
      channel: channelText, 
      termName: this.selectedTerm ? this.selectedTerm.indskr_consentTermName : topicData.itemName
    });
    
    const popoverOptions = {
      header: this.translate.instant('OPT_OUT'),
      message: message,
      confirmText: this.translate.instant('CONFIRM'),
    };

    const popover = await this.popoverCtrl.create({
      component: IndAlertMsgComponent,
      componentProps: popoverOptions,
      cssClass: this.device.isNativeApp ? 'native-ind-alert-msg' : 'ind-alert-msg',
      backdropDismiss: true,
    });
    
    popover.present();

    await popover.onDidDismiss().then(async (res: any) => {
      if (res.data && res.data.role && res.data.role == 'ok') {
        const loader = await this.loadingCtrl.create();
        
        // Prepare opt-out payload using topicData and selected values
        const optOutPayload = this._prepareOptOutPayloadFromActivity(topicData);
        
        await loader.present();
        this.consentService.createOptOutConsentActivity([optOutPayload]).then(async () => {
          await loader.dismiss();
          const preserveChannel = this.selectedChannel;
          // Refresh the view by reprocessing activities
          this.processConsentActivities();
          this.initTermTabs();
          this.initProductTabs();
          this.initSelectedChannel(preserveChannel);
        }, async error => {
          await loader.dismiss();
        });
      }
       else{
        topicData.isOptedIn = true;
        if (childRef) {
          childRef.revertTo(true);
        }
      }
    });
  }


  private _prepareOptOutPayloadFromActivity(topicData: ItemDetailCardDataModel): any {
    const payload = this._buildBaseOptOutPayload(this.getActivitiesForTopic(topicData.topicId));

    if (topicData.topicId) {
      payload['indskr_Topic@odata.bind'] = topicData.topicId;
      const allConsentTerms: ConsentTerm[] = this.consentService.allConsentTermsSubject.getValue();
      if (allConsentTerms && allConsentTerms.length > 0) {
        const termId = this.selectedTerm.indskr_consenttermsid || '';
        const consentTerm = allConsentTerms.find(t => {
          const tId = t.indskr_consenttermsid || '';
          return tId.toString() === termId.toString();
        });
        if (consentTerm && Array.isArray(consentTerm.topics) && consentTerm.topics.length > 0) {
          const topic = consentTerm.topics.find(t => t.indskr_topic === topicData.topicId);
          if (topic?.indskr_purpose) {
            payload['indskr_Purpose@odata.bind'] = topic.indskr_purpose;
          }
          if (topic?.indskr_complianceprofile) {
            payload['indskr_ComplianceProfile@odata.bind'] = topic.indskr_complianceprofile;
          }
        }
      }
    }
    return payload;
  }

  private refreshConsentsFromService() {
    if (this.isPending || !this.contactId) {
      return; // Don't refresh pending sync consents
    }
  
    const allActiveConsents = this.consentService.allActiveConsentSubject.getValue();
    if (allActiveConsents && allActiveConsents.length > 0) {
      const contactConsent = allActiveConsents.find(cc => 
        cc.indskr_contactid && cc.indskr_contactid.toString() === this.contactId.toString()
      );
      if (contactConsent && contactConsent.activeConsents) {
        // Filter to only active consents (not offline saved)
        const activeConsents = contactConsent.activeConsents.filter(consent => !consent.isOfflineSaved);
        
        // Update this.consents with fresh data
        this.consents = activeConsents;
        
        // Update selectedTerm to point to refreshed data
        if (this.selectedTerm) {
          const termId = this.selectedTerm.indskr_consenttermsid;
          const refreshedTerm = this.consents.find(c => {
            const cTermId = c.indskr_consenttermsid;
            return cTermId.toString() === termId.toString();
          });
          if (refreshedTerm) {
            this.selectedTerm = refreshedTerm;
          } else {
            if (this.consents.length > 0) {
              this.selectedTerm = this.consents[0];
              this.selectedTermIndex = "0";
            } else {
              // No terms available - this is an edge case
              this.selectedTerm = null;
              this.selectedTermIndex = "0";
            }
          }
        }
      }
    }
  }

}

